import dayjs from 'dayjs';
import { normalizeCountryName } from './countrySettings';
import { ConfirmationAction, HighRiskCountry, HomeCountryConflict, SortDirection, SortField } from './RiskyNations.types';

export const formatRiskyNationDate = (value?: string) => {
  if (!value) return null;

  const date = dayjs(value);
  return date.isValid() ? date.format('MMM D, YYYY') : null;
};

export const getLatestRiskyNationUpdate = (countries: HighRiskCountry[]) => {
  const timestamps = countries
    .flatMap(country => [country.lastUpdated, country.dateAdded])
    .filter((value): value is string => Boolean(value))
    .map(value => dayjs(value))
    .filter(value => value.isValid());

  if (!timestamps.length) return null;

  const latestTimestamp = timestamps.reduce((latest, current) => Math.max(latest, current.valueOf()), 0);
  return formatRiskyNationDate(dayjs(latestTimestamp).toISOString());
};

export const sortRiskyNations = (countries: HighRiskCountry[], sortField: SortField, sortDirection: SortDirection) => {
  const direction = sortDirection === 'ASC' ? 1 : -1;

  return [...countries].sort((first, second) => {
    if (sortField === 'dateAdded') {
      const dateDifference = dayjs(first.dateAdded || 0).valueOf() - dayjs(second.dateAdded || 0).valueOf();
      if (dateDifference) return dateDifference * direction;
    }

    return normalizeCountryName(first.name).localeCompare(normalizeCountryName(second.name)) * direction;
  });
};

export const isHomeCountryConflict = (value: unknown): value is HomeCountryConflict => {
  if (!value || typeof value !== 'object') return false;

  return (value as HomeCountryConflict).code === 'HOME_COUNTRY_RISKY_NATION_CONFLICT';
};

export const getConfirmationCopy = (action: ConfirmationAction | null): { title: string; body: string; confirmLabel: string } => {
  if (action?.kind === 'remove') {
    const countryName = normalizeCountryName(action.country.name);
    return {
      title: `Remove ${countryName} from the list?`,
      body: 'Connections made by apps to a server in that country will no longer be flagged as risky.',
      confirmLabel: 'Remove Country',
    };
  }

  if (action?.kind === 'removeMultiple') {
    const countryCount = action.countries.length;
    return {
      title: `Remove ${countryCount} ${countryCount === 1 ? 'country' : 'countries'} from the list?`,
      body:
        countryCount === 1
          ? 'Connections made by apps to a server in that country will no longer be flagged as risky.'
          : 'Connections made by apps to servers in those countries will no longer be flagged as risky.',
      confirmLabel: countryCount === 1 ? 'Remove Country' : 'Remove Countries',
    };
  }

  if (action?.kind === 'restore') {
    return {
      title: 'Restore defaults?',
      body: "The Risky Nations list will reset back to the default list of countries sanctioned by the US Department of Treasury's OFAC.",
      confirmLabel: 'Restore Defaults',
    };
  }

  return {
    title: 'Clear list?',
    body: 'No network connections will be flagged as risky.',
    confirmLabel: 'Clear List',
  };
};
